import React, { useState } from "react";
import { toast } from "sonner";// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Search, Download, ArrowUpFromLine, CheckCircle2, XCircle, Clock, Loader2, Plus, History } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import StatusBadge from "@/shared/components/merchant/StatusBadge";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { useLanguage } from "@/shared/components/shared/LanguageContext";



export default function ResellerWithdrawals() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { t, language } = useLanguage();
  const resellerName = "Asia Gaming Network";

  // TODO: Implement custom API calls
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["reseller-withdrawals"],
    queryFn: async () => [], // TODO: Implement Transaction.filter API call
  });

  const filteredTransactions = transactions.filter(tx => {
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && tx.status !== statusFilter) return false;
    return true;
  });

  const stats = React.useMemo(() => {
    const success = transactions.filter(t => t.status === "success");
    const pending = transactions.filter(t => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  }, [transactions]);

  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success(`Withdrawal request submitted successfully! Type: ${data.type}`);
  };

  return (
    <ResellerLayout currentPage="ResellerWithdrawals" resellerName={resellerName}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t('withdrawals')}</h1>
            <p className="text-slate-500">{t("requestPayoutsViaBankUsdt")}</p>
          </div>
        </div>

        <Tabs defaultValue="new" className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="new" className="gap-2 px-4">
              <Plus className="h-4 w-4" />
              {t("newWithdrawal")}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2 px-4">
              <History className="h-4 w-4" />
              {t("history")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new">
            <WithdrawalForm
              onSubmit={handleWithdrawalSubmit}
              submitting={submitting}
              stats={stats}
              accentColor="violet"
            />
          </TabsContent>

          <TabsContent value="history">
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-sm text-slate-500">{t('totalWithdrawn')}</p>
                <p className="text-2xl font-bold text-violet-600 mt-1">฿{stats.total.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-sm text-slate-500">{t('completed')}</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {transactions.filter(t => t.status === "success").length}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-sm text-slate-500">{t('pending')}</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pendingCount}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-4">
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input placeholder={t('search') + "..."} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder={t('status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allStatus')}</SelectItem>
                    <SelectItem value="success">{t('success')}</SelectItem>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="failed">{t('failed')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  {t('export')}
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('transaction')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t('merchant')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('method')}</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('amount')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('status')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t('date')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t('loading')}</td></tr>
                    ) : filteredTransactions.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t('noWithdrawalsFound')}</td></tr>
                    ) : (
                      filteredTransactions.slice(0, 50).map((tx, i) => {
                        return (
                          <motion.tr
                            key={tx.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.02 }}
                            onClick={() => setSelectedTx(tx)}
                            className="hover:bg-slate-50 cursor-pointer"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                                  <ArrowUpFromLine className="h-4 w-4 text-violet-600" />
                                </div>
                                <div>
                                  <p className="font-mono text-sm">{tx.transaction_id?.slice(0, 10)}...</p>
                                  <p className="text-xs text-slate-400 sm:hidden">{tx.merchant_code}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell text-sm">{tx.merchant_code || t('nA')}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="text-xs">
                                {tx.payment_method === "bank_transfer" ? t('bank') : "USDT"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-violet-600">฿{tx.amount?.toLocaleString()}</td>
                            <td className="px-4 py-3">
                              <StatusBadge status={tx.status} />
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 hidden md:table-cell">
                              {tx.created_date ? format(new Date(tx.created_date), language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS }) : t('nA')}
                            </td>
                          </motion.tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} />
      </div>
    </ResellerLayout>
  );
}